import {
  FILE_STATUS,
  FILE_STATUS_DISPLAY,
  type FileStatusId,
  isInternalUser,
  PARTY_STATUS,
  PARTY_STATUS_DISPLAY,
  PAYMENT_INSTRUCTION_STATUS,
  PAYMENT_INSTRUCTION_STATUS_DISPLAY,
  PAYMENT_STATUS,
  PAYMENT_STATUS_DISPLAY,
  type PartyStatusId,
  type PaymentInstructionStatusId,
  type PaymentStatusId,
} from '@steerfi/shared';
import { Badge } from '@/components/ui/badge';
import { useKeycloakAuth } from '@/contexts/keycloak-auth-context';
import { cn } from '@/lib/utils';

export enum PaymentStatus {
  PENDING = 'pending',
  PROCESSING = 'processing',
  CLIENT_APPROVED = 'client_approved',
  COMPLETED = 'completed',
  FAILED = 'failed',
  REJECTED = 'rejected',
  AWAITING_FUNDS = 'awaiting_funds',
  FUNDS_RECEIVED = 'funds_received',
  RELEASED = 'released',
  REISSUED = 'reissued',
  ACH_SENT = 'ach_sent',
  ACH_RECEIVED = 'ach_received',
  RECIPIENT_RECEIVED_FUNDS = 'recipient_received_funds',
  RECIPIENT_CARD_RECEIVED = 'recipient_card_received',
  RECIPIENT_CARD_ACTIVATED = 'recipient_card_activated',
  CHECK_SENT = 'check_sent',
  VIRTUAL_CARD_ISSUED = 'virtual_card_issued',
  DIGITAL_CASH_SENT = 'digital_cash_sent',
  PENDING_PAYMENT_INITIATION = 'pending_payment_initiation',
  ACH_PULL_INITIATED = 'ach_pull_initiated',
  ACH_PULL_PENDING = 'ach_pull_pending',
  WIRE_EXPECTED = 'wire_expected',
  FUNDS_RECONCILED = 'funds_reconciled',
  AR_PENDING_COLLECTION = 'ar_pending_collection',
  AR_ACH_PULL_SENT = 'ar_ach_pull_sent',
  AR_WIRE_REQUESTED = 'ar_wire_requested',
  AR_COLLECTION_FAILED = 'ar_collection_failed',
  AR_COLLECTED = 'ar_collected',
}

export enum SupplierStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  PENDING_VERIFICATION = 'PENDING_VERIFICATION',
  PROSPECT_PENDING = 'PROSPECT_PENDING',
}

export enum ClientStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  PENDING_VERIFICATION = 'PENDING_VERIFICATION',
  SUSPENDED = 'SUSPENDED',
}

// Reference Admin entities (currencies, countries, states/provinces)
export enum ReferenceAdminStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
}
export type PaymentFileStatus = PaymentStatus;
export const PaymentFileStatus = PaymentStatus;

export enum PaymentMethod {
  SECURE_CARD = 'secure_card',
  VIRTUAL_CARD = 'virtual_card',
  ACH = 'ach',
  ACH_PLUS = 'ach_plus',
  CHECK = 'check',
  DIGITAL_CASH = 'digital_cash',
}

export type StatusChipVariant =
  | 'default'
  | 'secondary'
  | 'destructive'
  | 'outline'
  | 'warning'
  | 'completed'
  | 'returned';

interface StatusChipProps {
  status: PaymentStatus | SupplierStatus | ClientStatus | ReferenceAdminStatus;
  type?: 'payment' | 'paymentFile' | 'supplier' | 'client' | 'reference';
  labelOverride?: string;
  variant?: StatusChipVariant;
}

/**
 * Returns the display label for a status ID using the shared constants from db-constants.ts
 * Supports Party, File, Payment, and Payment Instruction statuses
 */
const getStatusDisplay = (
  statusId: PartyStatusId | FileStatusId | PaymentStatusId | PaymentInstructionStatusId,
  statusType: 'party' | 'file' | 'payment' | 'paymentInstruction',
  isInternal?: boolean
): string => {
  // Internal vs non-internal label overrides for certain statuses
  if (statusType === 'file' && isInternal !== undefined) {
    if (statusId === FILE_STATUS.APPROVED) {
      return isInternal ? 'Client Approved' : 'Approved';
    }
    if (statusId === FILE_STATUS.FUNDS_RECEIVED && !isInternal) {
      return 'Processing';
    }
  }

  // Use the appropriate display constant based on status type
  switch (statusType) {
    case 'party':
      return PARTY_STATUS_DISPLAY[statusId as PartyStatusId] || 'Unknown';
    case 'file':
      return FILE_STATUS_DISPLAY[statusId as FileStatusId] || 'Unknown';
    case 'payment':
      return PAYMENT_STATUS_DISPLAY[statusId as PaymentStatusId] || 'Unknown';
    case 'paymentInstruction':
      return (
        PAYMENT_INSTRUCTION_STATUS_DISPLAY[statusId as PaymentInstructionStatusId] || 'Unknown'
      );
    default:
      return 'Unknown';
  }
};

export const COMPLETED_STATUS_STYLES = {
  /** Badge CVA variant — drives all badge/chip surfaces */
  badge: 'completed',
  /** Tailwind dot class — drives timeline and supplier dialog dots */
  dot: 'bg-black',
} as const;

/**
 * Returns the status color category for a status ID using the shared constants from db-constants.ts
 * Supports Party, File, Payment, and Payment Instruction statuses
 */
export const getStatusColor = (
  statusId: PartyStatusId | FileStatusId | PaymentStatusId | PaymentInstructionStatusId,
  statusType: 'party' | 'file' | 'payment' | 'paymentInstruction'
): 'default' | 'secondary' | 'destructive' | 'outline' | 'completed' | 'returned' => {
  // 'success' | 'error' | 'warning' | 'default'
  if (statusType === 'party') {
    switch (statusId) {
      case PARTY_STATUS.ACTIVE:
        return 'default';
      case PARTY_STATUS.INACTIVE:
      case PARTY_STATUS.SUSPENDED:
      case PARTY_STATUS.PROSPECT_FAILED:
        return 'destructive';
      case PARTY_STATUS.PENDING_VERIFICATION:
      case PARTY_STATUS.PROSPECT_PENDING:
        return 'secondary';
      default:
        return 'outline';
    }
  }

  if (statusType === 'file') {
    switch (statusId) {
      case FILE_STATUS.COMPLETED:
        return COMPLETED_STATUS_STYLES.badge;
      case FILE_STATUS.APPROVED:
      case FILE_STATUS.FUNDS_RECEIVED:
      case FILE_STATUS.RELEASED:
      case FILE_STATUS.PROCESSED:
        return 'default';
      case FILE_STATUS.FAILED:
      case FILE_STATUS.REJECTED:
      case FILE_STATUS.DISAPPROVED:
      case FILE_STATUS.NEEDS_REVIEW:
        return 'destructive';
      case FILE_STATUS.PROCESSING:
      case FILE_STATUS.VALIDATING:
      case FILE_STATUS.WAITING_FOR_APPROVAL:
      case FILE_STATUS.AWAITING_FUNDS:
      case FILE_STATUS.READY_FOR_RELEASE:
        return 'secondary';
      default:
        return 'outline';
    }
  }

  if (statusType === 'payment') {
    switch (statusId) {
      case PAYMENT_STATUS.COMPLETED:
        return COMPLETED_STATUS_STYLES.badge;
      case PAYMENT_STATUS.APPROVED:
      case PAYMENT_STATUS.RELEASED:
      case PAYMENT_STATUS.REISSUED:
      case PAYMENT_STATUS.FUNDS_RECEIVED:
      case PAYMENT_STATUS.ISSUED:
        return 'default';
      case PAYMENT_STATUS.FAILED:
      case PAYMENT_STATUS.REJECTED:
      case PAYMENT_STATUS.CANCELLED:
      case PAYMENT_STATUS.EXPIRED:
      case PAYMENT_STATUS.STOPPED:
        return 'destructive';
      case PAYMENT_STATUS.RETURNED:
        return 'returned';
      case PAYMENT_STATUS.PROCESSING:
      case PAYMENT_STATUS.PENDING_APPROVAL:
      case PAYMENT_STATUS.AWAITING_FUNDS:
      case PAYMENT_STATUS.PENDING_RELEASE:
        return 'secondary';
      default:
        return 'outline';
    }
  }

  if (statusType === 'paymentInstruction') {
    switch (statusId) {
      case PAYMENT_INSTRUCTION_STATUS.RELEASED:
      case PAYMENT_INSTRUCTION_STATUS.SETTLED:
      case PAYMENT_INSTRUCTION_STATUS.LOADED:
        return 'default';
      case PAYMENT_INSTRUCTION_STATUS.FAILED:
      case PAYMENT_INSTRUCTION_STATUS.REJECTED:
      case PAYMENT_INSTRUCTION_STATUS.CANCELLED:
        return 'destructive';
      case PAYMENT_INSTRUCTION_STATUS.PENDING:
      case PAYMENT_INSTRUCTION_STATUS.ON_HOLD:
        return 'secondary';
      default:
        return 'outline';
    }
  }

  return 'outline';
};

/**
 * Alias for getStatusDisplay to maintain consistent API naming.
 */
export const getStatusLabel = (
  statusId: PartyStatusId | FileStatusId | PaymentStatusId | PaymentInstructionStatusId,
  statusType: 'party' | 'file' | 'payment' | 'paymentInstruction',
  isInternal?: boolean
): string => {
  return getStatusDisplay(statusId, statusType, isInternal);
};

/**
 * Returns the display label for a given status, with optional isInternal/type overrides.
 * @deprecated Use getStatusDisplay with numeric status IDs instead
 */
const getStatusDisplayOld = (
  status: PaymentStatus | SupplierStatus | ClientStatus | ReferenceAdminStatus,
  isInternal?: boolean,
  type?: 'payment' | 'paymentFile' | 'supplier' | 'client' | 'reference'
): string => {
  if (type === 'supplier') {
    switch (status) {
      case SupplierStatus.ACTIVE:
        return 'Active';
      case SupplierStatus.INACTIVE:
        return 'Inactive';
      case SupplierStatus.PENDING_VERIFICATION:
        return 'Pending Verification';
      case SupplierStatus.PROSPECT_PENDING:
        return 'Prospect Pending';
      default:
        return status;
    }
  }
  if (type === 'client') {
    switch (status) {
      case ClientStatus.ACTIVE:
        return 'Active';
      case ClientStatus.INACTIVE:
        return 'Inactive';
      case ClientStatus.PENDING_VERIFICATION:
        return 'Pending Verification';
      case ClientStatus.SUSPENDED:
        return 'Suspended';
      default:
        return status;
    }
  }
  if (type === 'reference') {
    switch (status) {
      case ReferenceAdminStatus.ACTIVE:
        return 'Active';
      case ReferenceAdminStatus.INACTIVE:
        return 'Inactive';
      default:
        return String(status);
    }
  }

  // Internal vs non-internal label overrides
  if (type === 'paymentFile' && isInternal !== undefined) {
    if (status === PaymentStatus.CLIENT_APPROVED) {
      return isInternal ? 'Client Approved' : 'Approved';
    }
    if (status === PaymentStatus.FUNDS_RECEIVED && !isInternal) {
      return 'Processing';
    }
  }

  if (type === 'payment' && isInternal !== undefined) {
    if (status === PaymentStatus.CLIENT_APPROVED) {
      return isInternal ? 'Client Approved' : 'Approved';
    }
  }

  switch (status) {
    case PaymentStatus.PENDING:
      return 'Pending';
    case PaymentStatus.PROCESSING:
      return 'Processing';
    case PaymentStatus.CLIENT_APPROVED:
      return 'Client Approved';
    case PaymentStatus.COMPLETED:
      return 'Completed';
    case PaymentStatus.FAILED:
      return 'Failed';
    case PaymentStatus.REJECTED:
      return 'Rejected';
    case PaymentStatus.AWAITING_FUNDS:
      return 'Awaiting Funds';
    case PaymentStatus.FUNDS_RECEIVED:
      return 'Funds Received';
    case PaymentStatus.RELEASED:
      return 'Released';
    case PaymentStatus.REISSUED:
      return 'Reissued';
    case PaymentStatus.ACH_SENT:
      return 'ACH Sent';
    case PaymentStatus.ACH_RECEIVED:
      return 'ACH Received';
    case PaymentStatus.RECIPIENT_RECEIVED_FUNDS:
      return 'Recipient Received Funds';
    case PaymentStatus.RECIPIENT_CARD_RECEIVED:
      return 'Recipient Card Received';
    case PaymentStatus.RECIPIENT_CARD_ACTIVATED:
      return 'Recipient Card Activated';
    case PaymentStatus.CHECK_SENT:
      return 'Check Sent';
    case PaymentStatus.VIRTUAL_CARD_ISSUED:
      return 'Virtual Card Issued';
    case PaymentStatus.DIGITAL_CASH_SENT:
      return 'Digital Cash Sent';
    case PaymentStatus.PENDING_PAYMENT_INITIATION:
      return 'Pending Payment Initiation';
    case PaymentStatus.ACH_PULL_INITIATED:
      return 'ACH Pull Initiated';
    case PaymentStatus.ACH_PULL_PENDING:
      return 'ACH Pull Pending';
    case PaymentStatus.WIRE_EXPECTED:
      return 'Wire Expected';
    case PaymentStatus.FUNDS_RECONCILED:
      return 'Funds Reconciled';
    case PaymentStatus.AR_PENDING_COLLECTION:
      return 'Pending Collection';
    case PaymentStatus.AR_ACH_PULL_SENT:
      return 'ACH Pull Sent';
    case PaymentStatus.AR_WIRE_REQUESTED:
      return 'Wire Requested';
    case PaymentStatus.AR_COLLECTION_FAILED:
      return 'Collection Failed';
    case PaymentStatus.AR_COLLECTED:
      return 'Collected';
    default:
      return String(status);
  }
};

/**
 * Maps a status to a Badge variant for visual styling.
 */
const getStatusVariant = (
  status: PaymentStatus | SupplierStatus | ClientStatus | ReferenceAdminStatus
): 'default' | 'secondary' | 'destructive' | 'outline' | 'completed' => {
  if (Object.values(SupplierStatus).includes(status as SupplierStatus)) {
    switch (status) {
      case SupplierStatus.ACTIVE:
        return 'default';
      case SupplierStatus.INACTIVE:
        return 'destructive';
      case SupplierStatus.PENDING_VERIFICATION:
      case SupplierStatus.PROSPECT_PENDING:
        return 'secondary';
      default:
        return 'outline';
    }
  }
  if (Object.values(ClientStatus).includes(status as ClientStatus)) {
    switch (status) {
      case ClientStatus.ACTIVE:
        return 'default';
      case ClientStatus.INACTIVE:
        return 'destructive';
      case ClientStatus.PENDING_VERIFICATION:
        return 'secondary';
      case ClientStatus.SUSPENDED:
        return 'destructive';
      default:
        return 'outline';
    }
  }
  if (Object.values(ReferenceAdminStatus).includes(status as ReferenceAdminStatus)) {
    switch (status) {
      case ReferenceAdminStatus.ACTIVE:
        return 'default';
      case ReferenceAdminStatus.INACTIVE:
        return 'destructive';
      default:
        return 'outline';
    }
  }
  switch (status) {
    case PaymentStatus.COMPLETED:
      return COMPLETED_STATUS_STYLES.badge;
    case PaymentStatus.FUNDS_RECEIVED:
    case PaymentStatus.RELEASED:
    case PaymentStatus.REISSUED:
    case PaymentStatus.FUNDS_RECONCILED:
    case PaymentStatus.AR_COLLECTED:
      return 'default';
    case PaymentStatus.FAILED:
    case PaymentStatus.REJECTED:
    case PaymentStatus.AR_COLLECTION_FAILED:
      return 'destructive';
    case PaymentStatus.PROCESSING:
    case PaymentStatus.ACH_SENT:
    case PaymentStatus.CHECK_SENT:
    case PaymentStatus.VIRTUAL_CARD_ISSUED:
    case PaymentStatus.ACH_PULL_INITIATED:
    case PaymentStatus.ACH_PULL_PENDING:
    case PaymentStatus.AR_ACH_PULL_SENT:
      return 'secondary';
    default:
      return 'outline';
  }
};

/**
 * Presentational chip that renders a styled Badge for a given status.
 */
export const StatusChip = ({
  status,
  type = 'payment',
  labelOverride,
  variant: variantOverride,
}: StatusChipProps) => {
  const { user } = useKeycloakAuth();
  const isInternal = isInternalUser(user?.roles);
  const variant: StatusChipVariant = variantOverride ?? getStatusVariant(status);
  const fallbackLabel = getStatusDisplayOld(status, isInternal, type);
  const displayText = labelOverride ?? fallbackLabel;

  return (
    <Badge
      variant={variant}
      className={cn(
        'text-xs font-medium',
        variant === 'default' &&
          'bg-green-100 text-green-800 border-green-200 dark:bg-green-900/20 dark:text-green-400 dark:border-green-800',
        variant === 'destructive' &&
          'bg-red-100 text-red-800 border-red-200 dark:bg-red-900/20 dark:text-red-400 dark:border-red-800',
        variant === 'secondary' &&
          'bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/20 dark:text-blue-400 dark:border-blue-800'
      )}
    >
      {displayText}
    </Badge>
  );
};

/**
 * Converts PaymentMethod enum values into human-readable labels.
 */
export const getPaymentMethodDisplay = (method: PaymentMethod | string): string => {
  const methodStr = String(method).toLowerCase();
  switch (methodStr) {
    case 'unknown':
      return 'Unknown';
    case 'secure_card':
      return 'Secure Card';
    case 'virtual_card':
      return 'Virtual Card';
    case 'ach':
      return 'ACH';
    case 'ach_plus':
      return 'ACH+';
    case 'check':
      return 'Check';
    case 'digital_cash':
      return 'Digital Cash';
    default:
      return String(method);
  }
};

/**
 * Returns the color classes for a payment method/channel badge.
 */
const getPaymentMethodColor = (method: PaymentMethod | string): string => {
  const methodStr = String(method).toLowerCase();
  switch (methodStr) {
    case 'ach':
      return 'bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/20 dark:text-blue-400 dark:border-blue-800';
    case 'ach_plus':
      return 'bg-emerald-100 text-emerald-800 border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-400 dark:border-emerald-800';
    case 'check':
      return 'bg-secondary text-secondary-foreground border-border dark:bg-secondary/20 dark:text-muted-foreground';
    case 'virtual_card':
      return 'bg-indigo-100 text-indigo-800 border-indigo-200 dark:bg-indigo-900/20 dark:text-indigo-400 dark:border-indigo-800';
    case 'secure_card':
      return 'bg-purple-100 text-purple-800 border-purple-200 dark:bg-purple-900/20 dark:text-purple-400 dark:border-purple-800';
    case 'digital_cash':
      return 'bg-teal-100 text-teal-800 border-teal-200 dark:bg-teal-900/20 dark:text-teal-400 dark:border-teal-800';
    default:
      return 'bg-secondary text-secondary-foreground border-border dark:bg-secondary/20 dark:text-muted-foreground';
  }
};

/**
 * Presentational chip for payment methods/channels.
 */
interface ChannelChipProps {
  channel: PaymentMethod | string;
}

export const ChannelChip = ({ channel }: ChannelChipProps) => {
  const displayText = getPaymentMethodDisplay(channel);
  const colorClasses = getPaymentMethodColor(channel);

  return <Badge className={cn('text-xs font-medium', colorClasses)}>{displayText}</Badge>;
};
